/**
 * AiAnalytics — AI FinOps Dashboard
 *
 * Route: /ai-analytics
 * Auth:  Protected (global JWT middleware) — no AI feature-flag gate.
 *        Analytics are always accessible regardless of which AI features are enabled.
 *
 * Data: GET /api/organization/ai-analytics?days=${days}
 *
 * Sections:
 *  1. Page header + time-range / view-mode controls
 *  2. Three KPI summary cards (Total Tokens, Estimated Cost, Top Feature)
 *  3. Recharts StackedBarChart — tokens or USD cost per day, one Bar per feature
 *  4. Feature breakdown table — feature | tokens | cost | % share
 */

import { useState, useEffect } from 'react';
import {
    BarChart2, Loader2, AlertCircle, Zap, DollarSign, Trophy,
} from 'lucide-react';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
    CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
    FEATURE_COLORS,
    FEATURE_LABELS,
    type AiFeatureKey,
} from '../utils/ai-analytics-config';

// ── API base ──────────────────────────────────────────────────────────────────

const isProduction =
    window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
const API_URL = isProduction
    ? (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? window.location.origin
    : 'http://localhost:3000';

// ── Types ─────────────────────────────────────────────────────────────────────

interface IDailyFeatureBreakdown {
    tokens:  number;
    costUsd: number;
}

interface IDailySeriesEntry {
    date:             string;
    totalTokens:      number;
    estimatedCostUsd: number;
    byFeature:        Partial<Record<AiFeatureKey, IDailyFeatureBreakdown>>;
}

interface IByFeatureEntry {
    feature:          AiFeatureKey;
    totalTokens:      number;
    estimatedCostUsd: number;
}

interface ISummary {
    totalInputTokens:  number;
    totalOutputTokens: number;
    totalTokens:       number;
    estimatedCostUsd:  number;
    topFeature:        AiFeatureKey | null;
    topFeatureTokens:  number;
    daysInWindow:      number;
}

interface IAiAnalyticsData {
    dailySeries: IDailySeriesEntry[];
    byFeature:   IByFeatureEntry[];
    summary:     ISummary;
}

// ── Formatters ────────────────────────────────────────────────────────────────

function formatTokens(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
    if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
    return n.toLocaleString();
}

function formatCostUsd(n: number): string {
    if (n === 0)    return '$0.00';
    if (n >= 1)     return `$${n.toFixed(2)}`;
    if (n >= 0.001) return `$${n.toFixed(4)}`;
    return `$${n.toFixed(6)}`;
}

function formatDateTick(dateStr: string): string {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── Custom Recharts Tooltip ───────────────────────────────────────────────────

interface ITooltipPayloadEntry {
    name:  string;
    value: number;
    color: string;
}

interface ICustomTooltipProps {
    active?:  boolean;
    payload?: ITooltipPayloadEntry[];
    label?:   string;
    isDark:   boolean;
    viewMode: 'tokens' | 'cost';
}

function CustomTooltip({ active, payload, label, isDark, viewMode }: ICustomTooltipProps) {
    if (!active || !payload || payload.length === 0) return null;

    const total = payload.reduce((sum, p) => sum + (p.value ?? 0), 0);
    const bgColor    = isDark ? '#1e293b' : '#ffffff';
    const borderColor = isDark ? '#334155' : '#e2e8f0';
    const labelColor = isDark ? '#f1f5f9' : '#0f172a';
    const mutedColor = isDark ? '#94a3b8' : '#64748b';
    const dividerColor = isDark ? '#334155' : '#f1f5f9';

    return (
        <div style={{
            backgroundColor: bgColor,
            border: `1px solid ${borderColor}`,
            borderRadius: '10px',
            padding: '10px 14px',
            boxShadow: isDark
                ? '0 8px 24px rgba(0,0,0,0.5)'
                : '0 4px 16px rgba(0,0,0,0.1)',
            minWidth: '180px',
            fontSize: '12px',
        }}>
            <p style={{ color: labelColor, fontWeight: 600, marginBottom: 8 }}>
                {label ? formatDateTick(label) : ''}
            </p>
            {payload.map((entry) => (
                <div key={entry.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
                    <span style={{ color: entry.color, display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: entry.color, display: 'inline-block', flexShrink: 0 }} />
                        {FEATURE_LABELS[entry.name as AiFeatureKey] ?? entry.name}
                    </span>
                    <span style={{ color: labelColor, fontWeight: 500 }}>
                        {viewMode === 'cost'
                            ? formatCostUsd(entry.value)
                            : formatTokens(entry.value)
                        }
                    </span>
                </div>
            ))}
            <div style={{ borderTop: `1px solid ${dividerColor}`, marginTop: 8, paddingTop: 6, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: mutedColor, fontWeight: 500 }}>Total</span>
                <span style={{ color: labelColor, fontWeight: 700 }}>
                    {viewMode === 'cost' ? formatCostUsd(total) : formatTokens(total)}
                </span>
            </div>
        </div>
    );
}

// ── KPI Card ──────────────────────────────────────────────────────────────────

interface IKpiCardProps {
    icon:     React.ReactNode;
    title:    string;
    value:    string;
    sub:      string;
    accent:   string;  // tailwind bg class for the icon bg
}

function KpiCard({ icon, title, value, sub, accent }: IKpiCardProps) {
    return (
        <div className="bg-white dark:bg-gh-bg-dark border border-slate-200 dark:border-gh-border-dark rounded-xl p-5 flex items-start gap-4 shadow-sm">
            <div className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${accent}`}>
                {icon}
            </div>
            <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-0.5">
                    {title}
                </p>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-50 truncate leading-tight">
                    {value}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                    {sub}
                </p>
            </div>
        </div>
    );
}

// ── Empty / Loading / Error states ────────────────────────────────────────────

function EmptyState() {
    return (
        <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                <BarChart2 size={28} className="text-slate-400 dark:text-slate-500" />
            </div>
            <h3 className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-1">
                No usage data yet
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs">
                AI token usage will appear here once your team starts using AI-powered features.
            </p>
        </div>
    );
}

// ── Main page component ───────────────────────────────────────────────────────

type DaysOption  = 7 | 14 | 30 | 90;
type ViewMode    = 'tokens' | 'cost';

const DAYS_OPTIONS: DaysOption[] = [7, 14, 30, 90];

export function AiAnalytics() {
    const { token } = useAuth();
    const { theme }  = useTheme();
    const isDark     = theme === 'dark';

    const [days,     setDays]     = useState<DaysOption>(30);
    const [viewMode, setViewMode] = useState<ViewMode>('tokens');
    const [data,     setData]     = useState<IAiAnalyticsData | null>(null);
    const [loading,  setLoading]  = useState(true);
    const [error,    setError]    = useState<string | null>(null);

    // ── Fetch analytics data ───────────────────────────────────────────────────
    useEffect(() => {
        if (!token) return;

        setLoading(true);
        setError(null);

        fetch(`${API_URL}/api/organization/ai-analytics?days=${days}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => r.json())
            .then((res) => {
                if (res.success) {
                    setData(res.data as IAiAnalyticsData);
                } else {
                    setError(res.error ?? 'Failed to load analytics data');
                }
            })
            .catch(() => setError('Network error — could not reach the server'))
            .finally(() => setLoading(false));
    }, [token, days]);

    // ── Derive the set of features that have data ──────────────────────────────
    // We only render <Bar> components for features with non-zero token counts in
    // the current window — avoids cluttering the legend with empty entries.
    const activeFeatures: AiFeatureKey[] = data?.byFeature.map((f) => f.feature) ?? [];

    // ── Transform dailySeries → flat Recharts-compatible array ────────────────
    const chartData = (data?.dailySeries ?? []).map((day) => {
        const row: Record<string, string | number> = { date: day.date };
        for (const [feat, vals] of Object.entries(day.byFeature)) {
            row[feat] = viewMode === 'cost'
                ? (vals as IDailyFeatureBreakdown).costUsd
                : (vals as IDailyFeatureBreakdown).tokens;
        }
        return row;
    });

    // ── Summary values ─────────────────────────────────────────────────────────
    const summary = data?.summary;
    const totalTokensLabel  = summary ? formatTokens(summary.totalTokens)      : '—';
    const totalCostLabel    = summary ? formatCostUsd(summary.estimatedCostUsd) : '—';
    const topFeatureLabel   = summary?.topFeature ? FEATURE_LABELS[summary.topFeature] : 'None yet';
    const topFeatureSub     = summary?.topFeature
        ? `${formatTokens(summary.topFeatureTokens)} tokens consumed`
        : 'No AI activity in this period';

    const hasData = (data?.dailySeries.length ?? 0) > 0;

    // ── Total tokens for percentage calculation in the table ──────────────────
    const grandTotal = summary?.totalTokens ?? 0;

    // ── Recharts axis / grid colours (theme-aware) ────────────────────────────
    const tickColor   = isDark ? '#94a3b8' : '#64748b';
    const gridColor   = isDark ? '#1e293b' : '#f1f5f9';

    return (
        <div className="flex flex-col gap-6 p-4 md:p-6 max-w-[1400px] mx-auto">

            {/* ── Page header ─────────────────────────────────────────────── */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center flex-shrink-0">
                        <BarChart2 size={20} className="text-violet-600 dark:text-violet-400" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-slate-900 dark:text-slate-50 leading-tight">
                            AI Cost Analytics
                        </h1>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            Token usage and estimated spend across all AI features
                        </p>
                    </div>
                </div>

                {/* ── Controls: time range + view mode ──────────────────────── */}
                <div className="flex items-center gap-2 flex-wrap">
                    {/* View mode toggle */}
                    <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5 gap-0.5">
                        {(['tokens', 'cost'] as ViewMode[]).map((mode) => (
                            <button
                                key={mode}
                                onClick={() => setViewMode(mode)}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-150 ${
                                    viewMode === mode
                                        ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-50 shadow-sm'
                                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                                }`}
                            >
                                {mode === 'tokens' ? 'Tokens' : 'USD Cost'}
                            </button>
                        ))}
                    </div>

                    {/* Time range */}
                    <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5 gap-0.5">
                        {DAYS_OPTIONS.map((d) => (
                            <button
                                key={d}
                                onClick={() => setDays(d)}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-150 ${
                                    days === d
                                        ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-50 shadow-sm'
                                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                                }`}
                            >
                                {d}d
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Loading / error states ──────────────────────────────────── */}
            {loading && (
                <div className="flex items-center justify-center py-24">
                    <Loader2 size={28} className="animate-spin text-violet-500" />
                </div>
            )}

            {!loading && error && (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400">
                    <AlertCircle size={18} className="flex-shrink-0" />
                    <span className="text-sm font-medium">{error}</span>
                </div>
            )}

            {!loading && !error && (
                <>
                    {/* ── KPI Cards ──────────────────────────────────────────── */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <KpiCard
                            icon={<Zap size={20} className="text-violet-600 dark:text-violet-400" />}
                            accent="bg-violet-100 dark:bg-violet-900/40"
                            title={`Total Tokens (${days}d)`}
                            value={totalTokensLabel}
                            sub={summary
                                ? `${formatTokens(summary.totalInputTokens)} in · ${formatTokens(summary.totalOutputTokens)} out`
                                : 'No data'
                            }
                        />
                        <KpiCard
                            icon={<DollarSign size={20} className="text-emerald-600 dark:text-emerald-400" />}
                            accent="bg-emerald-100 dark:bg-emerald-900/40"
                            title="Estimated Cost"
                            value={totalCostLabel}
                            sub="Based on official provider pricing"
                        />
                        <KpiCard
                            icon={<Trophy size={20} className="text-amber-600 dark:text-amber-400" />}
                            accent="bg-amber-100 dark:bg-amber-900/40"
                            title="Top Consuming Feature"
                            value={topFeatureLabel}
                            sub={topFeatureSub}
                        />
                    </div>

                    {!hasData ? (
                        <EmptyState />
                    ) : (
                        <>
                            {/* ── Stacked Bar Chart ───────────────────────────── */}
                            <div className="bg-white dark:bg-gh-bg-dark border border-slate-200 dark:border-gh-border-dark rounded-xl p-5 shadow-sm">
                                <div className="flex items-center justify-between mb-5">
                                    <div>
                                        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                            {viewMode === 'tokens' ? 'Token Usage Over Time' : 'Estimated Cost Over Time'}
                                        </h2>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                            Stacked by feature · last {days} days
                                        </p>
                                    </div>
                                </div>

                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart
                                        data={chartData}
                                        margin={{ top: 4, right: 8, left: 4, bottom: 4 }}
                                        barCategoryGap="25%"
                                    >
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke={gridColor}
                                            vertical={false}
                                        />
                                        <XAxis
                                            dataKey="date"
                                            tickFormatter={formatDateTick}
                                            tick={{ fontSize: 11, fill: tickColor }}
                                            axisLine={false}
                                            tickLine={false}
                                            interval="preserveStartEnd"
                                        />
                                        <YAxis
                                            tickFormatter={viewMode === 'tokens'
                                                ? formatTokens
                                                : (v: number) => `$${v >= 0.01 ? v.toFixed(2) : v.toFixed(4)}`
                                            }
                                            tick={{ fontSize: 11, fill: tickColor }}
                                            axisLine={false}
                                            tickLine={false}
                                            width={60}
                                        />
                                        <Tooltip
                                            content={
                                                <CustomTooltip
                                                    isDark={isDark}
                                                    viewMode={viewMode}
                                                />
                                            }
                                            cursor={{ fill: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}
                                        />
                                        <Legend
                                            formatter={(value) =>
                                                FEATURE_LABELS[value as AiFeatureKey] ?? value
                                            }
                                            wrapperStyle={{ fontSize: 11, color: tickColor, paddingTop: 12 }}
                                            iconType="circle"
                                            iconSize={8}
                                        />
                                        {activeFeatures.map((feat) => (
                                            <Bar
                                                key={feat}
                                                dataKey={feat}
                                                stackId="a"
                                                fill={FEATURE_COLORS[feat]}
                                                name={feat}
                                                radius={
                                                    /* round the top of the last stacked bar only */
                                                    feat === activeFeatures[activeFeatures.length - 1]
                                                        ? [3, 3, 0, 0]
                                                        : [0, 0, 0, 0]
                                                }
                                            />
                                        ))}
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* ── Feature Breakdown Table ──────────────────────── */}
                            <div className="bg-white dark:bg-gh-bg-dark border border-slate-200 dark:border-gh-border-dark rounded-xl shadow-sm overflow-hidden">
                                <div className="px-5 py-4 border-b border-slate-100 dark:border-gh-border-dark">
                                    <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                        Feature Breakdown
                                    </h2>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                        All figures for the selected {days}-day window
                                    </p>
                                </div>

                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-slate-100 dark:border-gh-border-dark">
                                                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 w-1/2">
                                                    Feature
                                                </th>
                                                <th className="text-right px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                                                    Tokens
                                                </th>
                                                <th className="text-right px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                                                    Est. Cost
                                                </th>
                                                <th className="text-right px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 hidden sm:table-cell">
                                                    % Share
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data!.byFeature.map((entry, idx) => {
                                                const share = grandTotal > 0
                                                    ? Math.round((entry.totalTokens / grandTotal) * 100)
                                                    : 0;
                                                const color = FEATURE_COLORS[entry.feature];

                                                return (
                                                    <tr
                                                        key={entry.feature}
                                                        className={`transition-colors ${
                                                            idx % 2 === 0
                                                                ? 'bg-transparent'
                                                                : 'bg-slate-50/60 dark:bg-slate-800/30'
                                                        } hover:bg-slate-50 dark:hover:bg-slate-800/50`}
                                                    >
                                                        <td className="px-5 py-3">
                                                            <div className="flex items-center gap-2.5">
                                                                <span
                                                                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                                                    style={{ backgroundColor: color }}
                                                                />
                                                                <span className="font-medium text-slate-700 dark:text-slate-200">
                                                                    {FEATURE_LABELS[entry.feature]}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="px-5 py-3 text-right font-mono text-slate-600 dark:text-slate-300 text-xs">
                                                            {formatTokens(entry.totalTokens)}
                                                        </td>
                                                        <td className="px-5 py-3 text-right font-mono text-slate-600 dark:text-slate-300 text-xs">
                                                            {formatCostUsd(entry.estimatedCostUsd)}
                                                        </td>
                                                        <td className="px-5 py-3 text-right hidden sm:table-cell">
                                                            <div className="flex items-center justify-end gap-2">
                                                                <div className="w-16 h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                                                                    <div
                                                                        className="h-full rounded-full transition-all duration-500"
                                                                        style={{ width: `${share}%`, backgroundColor: color }}
                                                                    />
                                                                </div>
                                                                <span className="text-xs font-medium text-slate-500 dark:text-slate-400 w-7 text-right">
                                                                    {share}%
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>

                                        {/* ── Grand total row ─────────────────── */}
                                        <tfoot>
                                            <tr className="border-t-2 border-slate-200 dark:border-gh-border-dark bg-slate-50 dark:bg-slate-800/50">
                                                <td className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                                    Total
                                                </td>
                                                <td className="px-5 py-3 text-right font-mono font-bold text-slate-800 dark:text-slate-100 text-xs">
                                                    {formatTokens(grandTotal)}
                                                </td>
                                                <td className="px-5 py-3 text-right font-mono font-bold text-slate-800 dark:text-slate-100 text-xs">
                                                    {formatCostUsd(summary?.estimatedCostUsd ?? 0)}
                                                </td>
                                                <td className="hidden sm:table-cell" />
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
}
